/**
 * kisiDuzenlemeTalebiService.js — Edit Kişi Requests (backlog #749; backend #747, KisiDuzenlemeTalebiController, V143).
 *
 * A company login may change a worker's phone and e-mail (PATCH /personnel/{id}/kunye, kisiKunyeService.patchKunye). On a LOCKED card (KVKK
 * accepted, Kimlik Doğrulandı, or — for the phone — a verified phone) nothing is written: the PATCH answers 202 with `kod: DUZENLEME_TALEBI`,
 * `kilitSebepleri` and `talep`, and staff with page.kisi decide. This file is the request itself:
 *
 *   GET  /kisi-duzenleme-talepleri?durum&personId&page&size → { talepler[], toplam, bekleyenSayisi, page, size } (NO envelope), pending first.
 *        staff page.kisi: every company · a company login: PERSONEL READ, its own companies only.
 *   POST /kisi-duzenleme-talepleri/{id}/onayla               → { message, result, data: talep } (page.kisi; 404 · 409 not pending / merged card)
 *   POST /kisi-duzenleme-talepleri/{id}/reddet {redSebebi}   → same (page.kisi; reason required, at most 500)
 *   PUT  /kisi-duzenleme-talepleri/{id}/geri-cek             → same (the company's own pending one; PERSONEL WRITE)
 * talep: { id, personId, personAd, personSoyad, guncelTelefon, guncelEposta (the card NOW), firmaId, firmaAdi, talepEdenUserId, talepEdenAdi,
 *          telefonDegisir, eskiTelefon, yeniTelefon, epostaDegisir, eskiEposta, yeniEposta, kilitSebepleri[], durum 0-3, durumKodu,
 *          redSebebi, kararVerenUserId, kararVerenAdi, kararTarihi, olusturmaTarihi }
 * The backend's texts (Mesaj) answer in the request language; the lock codes and states are translated on the frontend (components.duzenlemeTalebi).
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/kisi-duzenleme-talepleri`;

/** `durum` numbers, as the backend sends them. */
export const TALEP_DURUMU = { BEKLIYOR: 0, ONAYLANDI: 1, REDDEDILDI: 2, GERI_CEKILDI: 3 };
/** `data.kod` of the 202 answer of PATCH /kunye. */
export const TALEP_KODU = 'DUZENLEME_TALEBI';
/** The reasons a change needs approval (`kilitSebepleri`). */
export const KILIT_SEBEPLERI = ['KVKK', 'KIMLIK', 'TELEFON_DOGRULANDI'];

/** Did PATCH /kunye answer 202 "sent to SAKA for approval" (instead of writing)? `sonuc` = kisiKunyeService.patchKunye's result. */
export const talepGonderildi = (sonuc) => sonuc?.kod === TALEP_KODU;

/** Locks that cover EVERY field of the card; TELEFON_DOGRULANDI locks the phone only (an e-mail change on such a card still writes at once). */
const KART_KILITLERI = ['KVKK', 'KIMLIK'];

/**
 * Re-opened #749 (2026-11-05): the backend REPLACES a company's earlier pending request for a person with the new one (it does not merge), and the
 * company sends one key per PATCH — so fixing the phone and then the e-mail of a locked card used to withdraw the phone fix. The other field of the
 * pending request therefore rides along in the new PATCH body (`{ eposta, telefon: <the pending new phone> }`) and the new request carries both.
 * Only when the pending request was locked by a card-wide lock (KVKK / Kimlik): then the new change becomes a request too. A phone-only lock
 * (TELEFON_DOGRULANDI) leaves an e-mail change to write at once and the pending phone request alone, so nothing rides along.
 * A key the caller already sends is never overwritten (the same field again replaces the old value, as before). Pure: returns
 * `{ govde, tasinan }`, `tasinan` = the pending request whose other field rides along (or null).
 */
export function govdeyeBekleyeniEkle(govde, bekleyen) {
  if (!bekleyen || bekleyen.durum !== TALEP_DURUMU.BEKLIYOR) return { govde, tasinan: null };
  if (!(bekleyen.kilitSebepleri || []).some((k) => KART_KILITLERI.includes(k))) return { govde, tasinan: null };
  const yeni = { ...govde };
  let tasindi = false;
  if (bekleyen.telefonDegisir && bekleyen.yeniTelefon && !('telefon' in yeni)) { yeni.telefon = bekleyen.yeniTelefon; tasindi = true; }
  if (bekleyen.epostaDegisir && bekleyen.yeniEposta && !('eposta' in yeni)) { yeni.eposta = bekleyen.yeniEposta; tasindi = true; }
  return tasindi ? { govde: yeni, tasinan: bekleyen } : { govde, tasinan: null };
}

/** The backend's own text of a refusal (it answers in the request language), else the transport's message. */
export function talepHatasi(error) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object' && data.message) return data.message;
  return error?.message || '';
}

const zarfData = (r) => (r.data && typeof r.data === 'object' && 'data' in r.data ? r.data.data : r.data);
const zarfMesaj = (r) => (r.data && typeof r.data === 'object' ? r.data.message ?? null : null);

/** One page of requests. `durum` omitted = every state; `personId` narrows to one kişi. */
export function getDuzenlemeTalepleri({ durum = null, personId = null, page = 0, size = 25 } = {}) {
  const params = { page, size };
  if (durum !== null && durum !== undefined && durum !== '') params.durum = durum;
  if (personId) params.personId = personId;
  return axios.get(BASE, { params, headers: getAuthHeader() }).then((r) => r.data);
}

/** The newest request that still has something to tell the company about this kişi: the pending one, else a rejected one (else null). */
export async function getGosterilecekTalep(personId) {
  const d = await getDuzenlemeTalepleri({ personId, size: 5 });
  const ilk = (d?.talepler || [])[0] || null; // pending first, then newest
  return ilk && (ilk.durum === TALEP_DURUMU.BEKLIYOR || ilk.durum === TALEP_DURUMU.REDDEDILDI) ? ilk : null;
}

/** Staff: approve (written through the künye editor as the approver). Resolves { talep, mesaj }. */
export const onaylaTalep = (id) =>
  axios.post(`${BASE}/${id}/onayla`, {}, { headers: getAuthHeader() }).then((r) => ({ talep: zarfData(r), mesaj: zarfMesaj(r) }));

/** Staff: reject with the reason the company will read (required, at most 500). */
export const reddetTalep = (id, redSebebi) =>
  axios.post(`${BASE}/${id}/reddet`, { redSebebi }, { headers: getAuthHeader() }).then((r) => ({ talep: zarfData(r), mesaj: zarfMesaj(r) }));

/** Company: withdraw its own pending request. */
export const geriCekTalep = (id) =>
  axios.put(`${BASE}/${id}/geri-cek`, null, { headers: getAuthHeader() }).then((r) => ({ talep: zarfData(r), mesaj: zarfMesaj(r) }));
